import { Error, ErrorOutline } from "@mui/icons-material";
import { CircularProgress } from "@mui/material";
import Hls from "hls.js";
import { use } from "react";
import { useEffect, useRef, useState } from "react";

const HLSPlayer = ({
  streamUrl,
  autoPlay = false,
  seekToLatest = false,
  playing = false,
  mute = false,
  startTime = 0,
  endTime = undefined,
  currentTime = 0,
  showControls = true,
  handleAutoPause = undefined,
  index = undefined,
  handleUpdateCurrentTime = undefined,
  setPlaytime = undefined,
  height,
  width,
}) => {
  const videoRef = useRef(null);
  const heightRef = useRef(height);
  const widthRef = useRef(width);
  const endTimeRef = useRef(endTime); // Ref to store the current endTime
  const currentTimeRef = useRef(currentTime); // Ref to store the current endTime
  const handleAutoPauseRef = useRef(handleAutoPause); // Ref to store the current endTime
  const handleUpdateCurrentTimeRef = useRef(handleUpdateCurrentTime); // Ref to store the current endTime
  const setPlaytimeRef = useRef(setPlaytime); // Ref to store the current endTime
  const indexRef = useRef(index); // Ref to store the current endTime
  const [isLoading, setIsLoading] = useState(false); // State to track
  const [error, setError] = useState("");

  useEffect(() => {
    if (endTime) endTimeRef.current = endTime;
  }, [endTime]);

  useEffect(() => {
    if (currentTime) currentTimeRef.current = currentTime;
  }, [currentTime]);

  useEffect(() => {
    if (handleAutoPause) handleAutoPauseRef.current = handleAutoPause;
  }, [handleAutoPause]);

  useEffect(() => {
    if (handleUpdateCurrentTime)
      handleUpdateCurrentTimeRef.current = handleUpdateCurrentTime;
  }, [handleUpdateCurrentTime]);

  useEffect(() => {
    if (setPlaytime) setPlaytimeRef.current = setPlaytime;
  }, [setPlaytime]);

  useEffect(() => {
    indexRef.current = index;
  }, [index]);

  useEffect(() => {
    heightRef.current = height;
  }, [height]);

  useEffect(() => {
    widthRef.current = width;
  }, [width]);

  useEffect(() => {
    if (videoRef.current) {
      if (playing) {
        videoRef.current.play();
      } else {
        videoRef.current.pause();
      }
    }
  }, [playing]);

  useEffect(() => {
    let hls;
    const videoElement = videoRef.current;

    if (streamUrl?.includes("blob")) {
      let hls;

      // Check if HLS is supported
      if (Hls.isSupported()) {
        if (seekToLatest) {
          hls = new Hls({
            liveSyncDurationCount: 3, // Live synchronization duration (default 3), adjust as needed
            liveMaxLatencyDurationCount: 5, // Max buffer latency to keep player at live edge
            startLevel: -1, // Automatically select the highest available level
          });
        } else {
          hls = new Hls({
            startLevel: -1,
            maxBufferLength: 30,
            maxBufferSize: 60 * 1000 * 1000,
            maxBufferHole: 0.5,
            capLevelToPlayerSize: true,
            autoStartLoad: true,
            startPosition: startTime,
          });
        }

        // Load the stream URL
        if (streamUrl) {
          setIsLoading(true);
          try {
            hls.loadSource(streamUrl);
            setError("");
          } catch (error) {
            console.log("error", error);
            setIsLoading(false);
            setError("Oops, something went wrong !");
            // setError("There was an issue loading the video !");
          }
        }
        hls.attachMedia(videoElement);

        // Event listener when the manifest is parsed
        hls.on(Hls.Events.MANIFEST_PARSED, () => {});

        hls.on(Hls.Events.LEVEL_LOADED, () => {
          console.log("Video level loaded");
        });

        // Event listener for when the fragment changes
        hls.on(Hls.Events.FRAG_CHANGED, (event, data) => {
          console.log("Fragment changed:", data.frag);
          setIsLoading(false);
        });

        // Event listeners for error handling
        hls.on(Hls.Events.ERROR, (event, data) => {
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                console.error("A network error occurred");
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                console.error("A media error occurred");
                break;
              case Hls.ErrorTypes.OTHER_ERROR:
                console.error("An unknown error occurred");
                break;
              default:
                break;
            }
          }
        });

        // Handle the endTime to pause the video
        const handleTimeUpdate = () => {
          if (
            endTimeRef.current &&
            videoElement.currentTime >= endTimeRef.current
          ) {
            videoElement.pause();
            hls.loadSource(streamUrl);
            if (handleAutoPauseRef && indexRef && handleAutoPause && index) {
              handleAutoPauseRef.current(index);
            } else if (handleAutoPauseRef && handleAutoPause) {
              handleAutoPauseRef.current();
            }
          }
        };

        // Add time update listener to handle pausing at endTime
        if (videoElement)
          videoElement.addEventListener("timeupdate", handleTimeUpdate);
      } else if (videoElement.canPlayType("application/vnd.apple.mpegurl")) {
        // Native support for HLS in Safari (no need for hls.js)
        videoElement.src = streamUrl;
      }
      return () => {
        if (hls) {
          hls.destroy();
        }
      };
    } else {
      // alert("Invalid stream URL");/
      if (videoElement) {
        const handleTimeUpdateMP4 = () => {
          if (
            endTimeRef.current &&
            videoElement.currentTime >= endTimeRef.current
          ) {
            videoElement.pause();
            // hls.loadSource(streamUrl);
            if (handleAutoPauseRef && indexRef && handleAutoPause && index) {
              handleAutoPauseRef.current(index);
            } else if (handleAutoPauseRef && handleAutoPause) {
              handleAutoPauseRef.current();
            }
          }
        };
        setIsLoading(true);
        videoElement.src = streamUrl;
        videoElement.currentTime = startTime;
        videoElement.addEventListener("timeupdate", handleTimeUpdateMP4);
      }
    }
    // Cleanup on component unmount
  }, [startTime, endTime, streamUrl]);
  // useEffect(() => {
  //   if(handleUpdateCurrentTime)
  //   handleUpdateCurrentTime(indexRef, Math.round(currentTime + 1));
  // }, [currentTime]);

  useEffect(() => {
    if (videoRef.current) {
      if (playing) {
        videoRef.current.play();
      } else {
        videoRef.current.pause();
      }
    }
  }, [playing]);

  useEffect(() => {
    const videoElement = videoRef.current;

    if (streamUrl?.includes("blob")) {
      let hls;

      // Check if HLS is supported
      if (Hls.isSupported()) {
        if (seekToLatest) {
          hls = new Hls({
            liveSyncDurationCount: 3, // Live synchronization duration (default 3), adjust as needed
            liveMaxLatencyDurationCount: 5, // Max buffer latency to keep player at live edge
            startLevel: -1, // Automatically select the highest available level
          });
        } else {
          hls = new Hls({
            startLevel: -1,
            maxBufferLength: 30,
            maxBufferSize: 60 * 1000 * 1000,
            maxBufferHole: 0.5,
            capLevelToPlayerSize: true,
            autoStartLoad: true,
            startPosition: startTime,
          });
        }

        // Load the stream URL
        if (streamUrl) {
          setIsLoading(true);
          try {
            hls.loadSource(streamUrl);
            setError("");
          } catch (error) {
            console.log("error", error);
            setIsLoading(false);
            setError("Oops, something went wrong !");
            // setError("There was an issue loading the video !");
          }
        }
        hls.attachMedia(videoElement);

        // Event listener when the manifest is parsed
        hls.on(Hls.Events.MANIFEST_PARSED, () => {});

        hls.on(Hls.Events.LEVEL_LOADED, () => {
          console.log("Video level loaded");
        });

        // Event listener for when the fragment changes
        hls.on(Hls.Events.FRAG_CHANGED, (event, data) => {
          console.log("Fragment changed:", data.frag);
          setIsLoading(false);
        });

        // Event listeners for error handling
        hls.on(Hls.Events.ERROR, (event, data) => {
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                console.error("A network error occurred");
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                console.error("A media error occurred");
                break;
              case Hls.ErrorTypes.OTHER_ERROR:
                console.error("An unknown error occurred");
                break;
              default:
                break;
            }
          }
        });

        // Handle the endTime to pause the video
        const handleTimeUpdate = () => {
          if (
            endTimeRef.current &&
            videoElement.currentTime >= endTimeRef.current
          ) {
            videoElement.pause();
            hls.loadSource(streamUrl);
            if (handleAutoPauseRef && indexRef && handleAutoPause && index) {
              handleAutoPauseRef.current(index);
            } else if (handleAutoPauseRef && handleAutoPause) {
              handleAutoPauseRef.current();
            }
          }
        };

        // Add time update listener to handle pausing at endTime
        if (videoElement)
          videoElement.addEventListener("timeupdate", handleTimeUpdate);
      } else if (videoElement.canPlayType("application/vnd.apple.mpegurl")) {
        // Native support for HLS in Safari (no need for hls.js)
        videoElement.src = streamUrl;
      }
      return () => {
        if (hls) {
          hls.destroy();
        }
      };
    } else {
      // alert("Invalid stream URL");/
      if (videoElement) {
        const handleTimeUpdateMP4 = () => {
          if (
            endTimeRef.current &&
            videoElement.currentTime >= endTimeRef.current
          ) {
            videoElement.pause();
            // hls.loadSource(streamUrl);
            if (handleAutoPauseRef && indexRef && handleAutoPause && index) {
              handleAutoPauseRef.current(index);
            } else if (handleAutoPauseRef && handleAutoPause) {
              handleAutoPauseRef.current();
            }
          }
        };
        setIsLoading(true);
        videoElement.src = streamUrl;
        videoElement.currentTime = startTime;
        videoElement.addEventListener("timeupdate", handleTimeUpdateMP4);
      }
    }
    // Cleanup on component unmount
  }, [startTime, endTime, streamUrl]);
  // useEffect(() => {
  //   if(handleUpdateCurrentTime)
  //   handleUpdateCurrentTime(indexRef, Math.round(currentTime + 1));
  // }, [currentTime]);

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "black",
      }}
    >
      {isLoading && (
        <CircularProgress
          size={36}
          thickness={4}
          sx={{
            marginLeft: "12px",
            verticalAlign: "middle",
            color: "#AC1FFF",
          }}
        />
      )}
      {error?.length > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.5rem",
          }}
        >
          <Error color="warning" />
          {error}
        </div>
      )}
      {!error && (
        <video
          ref={videoRef}
          controls={showControls}
          autoPlay={autoPlay}
          muted={mute}
          width={"100%"}
          height={"100%"}
          style={{ display: isLoading ? "none" : "block" }}
          onTimeUpdate={(val) => {
            if (handleUpdateCurrentTimeRef && handleUpdateCurrentTime) {
              handleUpdateCurrentTimeRef.current(
                // indexRef.current,
                // Math.round(currentTimeRef.current + 1),
                Math.round(videoRef.current.currentTime - startTime)
              );
            }
            if (setPlaytimeRef && setPlaytime) {
              if (videoRef.current.currentTime !== 0) {
                setPlaytimeRef.current(
                  videoRef.current.currentTime - startTime
                );
              }
            }
          }}
          onCanPlay={() => {
            setIsLoading(false);
          }}
        >
          Your browser does not support the video tag.
        </video>
      )}
    </div>
  );
};
export default HLSPlayer;
